import { UseQueryResult } from '@tanstack/react-query';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
	IImpersonationContextComponentProps,
	IModalContext,
	ModalChildComponentContextKey,
} from '../../../../../models';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { useCheckDomainAvailability, useRequestEmailDomain, useSuggestedEmailDomain } from '../../../../../queries';
import { IAccountFeatures } from '../../../../../viewmodels/AppViewModels';
import { brandPrimary, destructiveHover } from '../../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../../../web/components/Modal';
import { InfoIcon } from '../../../../../web/components/svgs/icons/InfoIcon';
import { SuccessCheckIcon } from '../../../../../web/components/svgs/icons/SuccessCheckIcon';
import { XIcon } from '../../../../../web/components/svgs/icons/XIcon';
import { TextInput } from '../../../../../web/components/TextInput';
import { TinyPopover } from '../../../../../web/components/TinyPopover';
import { styleSheet } from './styles';

interface IProps extends IModalContext<void>, IImpersonationContextComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	features?: IAccountFeatures | undefined;
	onDomainRegistered?: () => void;
}

interface IState {
	domainNameAvailability: boolean | null;
	domainName: string;
	isDomainNameDisabled: boolean;
	infoIsOpen: boolean;
	isRegistering: boolean;
}

const _EmailDomainModal = (props: IProps) => {
	const errorMessages = useErrorMessages();
	const [state, setState] = React.useState<IState>({
		domainNameAvailability: null,
		domainName: '',
		isDomainNameDisabled: false,
		infoIsOpen: false,
		isRegistering: false,
	});
	const domainNameRef = React.useRef<string>('');
	const { className, styles, parentModal, features, onDomainRegistered } = props;

	const { logApiError } = useEventLogging('EmailDomainModal');

	// Get suggested domain name
	const domainNameQuery: UseQueryResult<string | undefined, unknown> = useSuggestedEmailDomain({
		enabled: features?.sendEmail?.domainName ? false : true,
		impersonationContext: props.impersonationContext,
	});

	// Register email domain mutation
	const registerDomainMutation = useRequestEmailDomain({
		impersonationContext: props.impersonationContext,
		onError: error => {
			logApiError('RegisterDomain-Error', error);
			errorMessages.pushApiError(error);
			setState(prevState => ({
				...prevState,
				isRegistering: false,
			}));
		},
		onSuccess: data => {
			setState(prevState => ({
				...prevState,
				domainNameAvailability: null,
				isDomainNameDisabled: true,
				domainName: `${data.name}`,
				isRegistering: false,
			}));
			domainNameRef.current = `${data.name}`;
			toast.success('Email domain registered successfully!');
			onDomainRegistered?.();
			parentModal?.onRequestClose(undefined, false);
		},
	});

	const checkAvailabilityMutation = useCheckDomainAvailability({
		impersonationContext: props.impersonationContext,
		onError: error => {
			logApiError('CheckAvailability-Error', error);
			toast.error(error?.systemMessage);
			setState(prevState => ({
				...prevState,
				domainNameAvailability: false,
			}));
		},
		onSuccess: data => {
			setState(prevState => ({
				...prevState,
				domainNameAvailability: data,
			}));
		},
	});

	React.useEffect(() => {
		if (domainNameQuery?.data && !state.domainName) {
			setState(prevState => ({
				...prevState,
				domainName: domainNameQuery.data ?? '',
				isDomainNameDisabled: false,
			}));
			domainNameRef.current = domainNameQuery.data ?? '';
		}
	}, [domainNameQuery?.data, state.domainName]);

	React.useEffect(() => {
		if (features?.sendEmail?.domainName) {
			setState(prevState => ({
				...prevState,
				domainName: features.sendEmail.domainName ?? '',
				isDomainNameDisabled: true,
			}));
			domainNameRef.current = features.sendEmail.domainName ?? '';
		}
	}, [features?.sendEmail?.domainName]);

	const handleSave = () => {
		if (!state.domainNameAvailability) {
			toast.error('Please check domain name availability before registering.');
			return;
		}

		setState(prevState => ({ ...prevState, isRegistering: true }));
		registerDomainMutation.mutate({ domainName: state.domainName });
	};

	const handleDomainNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.persist();
		setState(prevState => ({
			...prevState,
			domainName: e?.target?.value.split(' ').join('-'),
			domainNameAvailability: null,
		}));
	};

	const handleCheckAvailability = () => {
		checkAvailabilityMutation.mutate({
			domainName: state.domainName,
		});
	};

	const handleReset = () => {
		setState(prevState => ({
			...prevState,
			domainName: domainNameRef.current,
			domainNameAvailability: null,
			isDomainNameDisabled: false,
		}));
	};

	const handleCancel = () => {
		parentModal?.onRequestClose(undefined, false);
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} email-domain-modal ${className || ''}`}>
			<div className={css(styleSheet.title)}>Request Email Domain</div>
			<div className={css(styleSheet.description)}>
				Configure a dedicated email domain for sending emails from Levitate&apos;s servers. This allows accounts with
				consumer email providers (Gmail, Yahoo, etc.) to send group emails.
			</div>

			{domainNameQuery.isPending && domainNameQuery.isFetching ? (
				<div className={css(styleSheet.loaderWrap)}>
					<LoadingSpinner />
				</div>
			) : (
				<div className={css(styleSheet.domainNameWrap)}>
					<div className={css(styleSheet.domainNameInputWrap)}>
						<div>
							<label
								className={css(styleSheet.domainNameLabel)}
								htmlFor='domainName'
								onMouseEnter={() => setState(prevState => ({ ...prevState, infoIsOpen: true }))}
							>
								<span>Domain Name</span> <span className={css(styleSheet.errorRequired)}>*</span>
								{!features?.sendEmail?.domainName ? (
									<div className={css(styleSheet.popoverWrap)}>
										<TinyPopover
											anchor={
												<figure className={css(styleSheet.infoIcon)}>
													<InfoIcon fillColor={brandPrimary} />
												</figure>
											}
											isOpen={state.infoIsOpen}
											dismissOnOutsideAction={true}
											onRequestClose={() => setState(prevState => ({ ...prevState, infoIsOpen: false }))}
											align='center'
											placement={['top', 'bottom', 'left', 'right']}
										>
											<p className={css(styleSheet.infoMessage)}>
												Domain name availability is not guaranteed. A domain name availability check is needed before
												the domain name can be registered.
											</p>
										</TinyPopover>
									</div>
								) : null}
							</label>
						</div>
						<div className={css(styleSheet.inputWrap)}>
							<TextInput
								type='text'
								inputId='domainName'
								value={state.domainName}
								placeholder='example.com'
								className={`${css(styleSheet.input)} ${
									state.domainNameAvailability === false ? css(styleSheet.domainNameInputError) : ''
								}`}
								disabled={state.isDomainNameDisabled}
								onChange={handleDomainNameChange}
							/>
							{state.domainNameAvailability ? (
								<figure className={css(styleSheet.availabilityCheckIcon)}>
									<SuccessCheckIcon />
								</figure>
							) : null}
							{state.domainNameAvailability === false ? (
								<figure className={css(styleSheet.availabilityCheckIcon)}>
									<XIcon color={destructiveHover} height={12} width={12} className={css(styleSheet.xIcon)} />
								</figure>
							) : null}
						</div>
					</div>
					{!features?.sendEmail?.domainName ? (
						<div className={css(styleSheet.availabilityCheckButtonWrap)}>
							{domainNameQuery?.data && state.isDomainNameDisabled ? (
								<button
									type='button'
									className={css(styleSheet.domainNameActionButton)}
									onClick={handleReset}
									disabled={state.isRegistering}
								>
									Use Different Domain
								</button>
							) : null}
							<button
								type='button'
								className={css(styleSheet.domainNameActionButton)}
								onClick={handleCheckAvailability}
								disabled={!state.domainName || state.isRegistering}
							>
								Check Availability
							</button>
						</div>
					) : null}
				</div>
			)}

			<div className={css(styleSheet.actionButtonsWrap)}>
				<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={handleCancel} disabled={state.isRegistering}>
					Cancel
				</button>
				{!features?.sendEmail?.domainName ? (
					<button
						className={css(baseStyleSheet.ctaButton)}
						onClick={handleSave}
						disabled={!state.domainNameAvailability || state.isRegistering}
					>
						{state.isRegistering ? 'Registering...' : 'Register Domain'}
					</button>
				) : null}
			</div>
		</div>
	);
};

export const EmailDomainModal = asModalComponent(inject(ModalChildComponentContextKey)(observer(_EmailDomainModal)), {
	className: css(styleSheet.modal),
	shouldCloseOnOverlayClick: false,
});
